import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { environment } from '@fitness-force/environments';
import {
  ListClientMilestoneProgressResponse,
  ClientMilestoneAchievementResponsePagedResults,
  CreateClientMilestoneAchievementRequest
} from '../models/milestone.models';
import { AppErrors } from '@fitness-force/models';

@Injectable({ providedIn: 'root' })
export class ClientMilestoneService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.BASE_URL;

  /**
   * Gets all milestone progress for a specific client (B2B staff view).
   * @param clientId - The client ID
   * @returns Observable of client milestone progress
   */
  getClientMilestoneProgress(clientId: number): Observable<ListClientMilestoneProgressResponse> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.CLIENT_MILESTONES.GET_PROGRESS
      .replace('{clientId}', clientId.toString());
    
    return this.http.get<ListClientMilestoneProgressResponse>(url).pipe(
      map((response: ListClientMilestoneProgressResponse) => {
        return response;
      }),
      catchError((error: any) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /**
   * Gets paged milestone achievements for a specific client (B2B staff view).
   * @param clientId - The client ID
   * @param pageNumber - Page number (default: 1)
   * @param pageSize - Page size (default: 20)
   * @returns Observable of paged client milestone achievements
   */
  getClientMilestoneAchievements(
    clientId: number,
    pageNumber: number = 1,
    pageSize: number = 20
  ): Observable<ClientMilestoneAchievementResponsePagedResults> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.CLIENT_MILESTONES.GET_ACHIEVEMENTS
      .replace('{clientId}', clientId.toString());
    
    const httpParams = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ClientMilestoneAchievementResponsePagedResults>(url, { params: httpParams }).pipe(
      map((response: ClientMilestoneAchievementResponsePagedResults) => {
        return response;
      }),
      catchError((error: any) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /**
   * Manually awards a milestone achievement to a specific client.
   * @param clientId - The client ID
   * @param request - The award request data
   * @returns Observable of the create response
   */
  manuallyAwardMilestone(
    clientId: number,
    request: CreateClientMilestoneAchievementRequest
  ): Observable<{ id: number }> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.CLIENT_MILESTONES.MANUALLY_AWARD
      .replace('{clientId}', clientId.toString());
    
    return this.http.post<{ id: number }>(url, request).pipe(
      map((response: { id: number }) => {
        return response;
      }),
      catchError((error: any) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /**
   * Revokes (deletes) a milestone achievement from a specific client.
   * @param clientId - The client ID
   * @param achievementId - The achievement ID to revoke
   * @returns Observable of void
   */
  revokeClientMilestoneAchievement(
    clientId: number,
    achievementId: number
  ): Observable<void> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.CLIENT_MILESTONES.REVOKE_ACHIEVEMENT
      .replace('{clientId}', clientId.toString())
      .replace('{achievementId}', achievementId.toString());
    
    return this.http.delete<void>(url).pipe(
      map((response: void) => {
        return response;
      }),
      catchError((error: any) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }
}
